import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Il "?" accanto a una voce: apre una spiegazione breve con un esempio concreto.
 * Si apre al clic (anche da telefono), si chiude con un clic fuori o con Esc.
 */
export function HelpTip({ title, text, example, size = 15 }: { title: string; text: React.ReactNode; example?: React.ReactNode; size?: number }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const r = btn.current?.getBoundingClientRect();
    if (r) {
      const width = 320;
      const left = Math.max(12, Math.min(window.innerWidth - width - 12, r.left - 20));
      const below = r.bottom + 8;
      setPos({ top: below + 220 > window.innerHeight ? Math.max(12, r.top - 228) : below, left });
    }
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        aria-label={`Aiuto: ${title}`}
        aria-expanded={open}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
        className={`inline-flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${open ? 'bg-[rgba(55,53,47,0.12)] text-[#37352F] dark:bg-white/15 dark:text-white' : 'border border-[rgba(55,53,47,0.3)] font-medium text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:border-white/30 dark:text-white/60'}`}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.6) }}
      >
        ?
      </button>
      {open && pos && createPortal(
        <div
          ref={box}
          role="tooltip"
          className="fixed z-[1000] w-80 rounded-md border border-[rgba(55,53,47,0.09)] bg-white p-3 text-[13px] leading-relaxed text-[#37352F] shadow-md dark:border-white/10 dark:bg-[#252525] dark:text-white/90"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="mb-1 text-[13px] font-medium">{title}</div>
          <div>{text}</div>
          {example && (
            <div className="mt-2 border-t border-[rgba(55,53,47,0.09)] pt-2 text-[#787774] dark:border-white/10 dark:text-white/60">
              <span className="font-medium text-[#37352F] dark:text-white/90">Esempio: </span>{example}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
